import { CalendarEvent, CalendarQuery, Venue, EventTypeCalendar, DayEvent, EventDetails, CreateEventRequest, UpdateEventRequest, EventParticipant, CreateEventSeriesRequest, ExtendEventSeriesRequest, EventSeriesPreview, EventSeriesCreated, EventSeriesDeleteResult } from '../types/calendar';
import api from './authService';

export const calendarService = {
  async getCalendarEvents(query: CalendarQuery): Promise<CalendarEvent[]> {
    const response = await api.get('/calendar/events', {
      params: {
        startDate: query.startDate,
        endDate: query.endDate,
        eventTypeId: query.eventTypeId,
        venueId: query.venueId,
        availableOnly: query.availableOnly,
        excludeFullOthers: query.excludeFullOthers,
        coachIds: query.coachIds?.join(',') || undefined
      }
    });
    return response.data;
  },

  async getDailyEvents(date: string, eventTypeId?: string): Promise<CalendarEvent[]> {
    const response = await api.get('/calendar/events/daily', {
      params: {
        date,
        eventTypeId
      }
    });
    return response.data;
  },

  async getActiveVenues(): Promise<Venue[]> {
    const response = await api.get('/calendar/venues');
    return response.data;
  },

  async getActiveEventTypes(): Promise<EventTypeCalendar[]> {
    const response = await api.get('/calendar/event-types');
    return response.data;
  },

  async getMonthlySummary(
    year: number,
    month: number,
    venueId?: string,
    availableOnly?: boolean,
    eventTypeIds?: string[],
    coachIds?: string[],
    excludeFullOthers?: boolean
  ): Promise<DayEvent[]> {
    const response = await api.get(`/calendar/monthly-summary/${year}/${month}`, {
      params: {
        venueId,
        availableOnly,
        eventTypeIds: eventTypeIds?.join(','),
        coachIds: coachIds?.join(','),
        excludeFullOthers
      }
    });
    return response.data;
  },

  // Event management functions - using the correct events API
  async getEventById(eventId: string): Promise<EventDetails> {
    const response = await api.get(`/events/${eventId}`);
    return response.data;
  },

  async createEvent(eventData: CreateEventRequest): Promise<EventDetails> {
    console.log('CalendarService: Creating event with data:', eventData);
    console.log('CalendarService: API base URL:', api.defaults.baseURL);

    try {
      const response = await api.post('/events', eventData);
      console.log('CalendarService: Event created successfully, response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('CalendarService: Error creating event:', error);
      console.error('CalendarService: Error response:', error.response);
      console.error('CalendarService: Error config:', error.config);
      throw error;
    }
  },

  async updateEvent(eventId: string, eventData: UpdateEventRequest): Promise<EventDetails> {
    const response = await api.put(`/events/${eventId}`, eventData);
    return response.data;
  },

  async deleteEvent(eventId: string): Promise<void> {
    await api.delete(`/events/${eventId}`);
  },

  /**
   * [magánóra-sorozat, 2026-10-31] Ismétlődő sorozat EGYETLEN kéréssel, a szerveren (admin ÉS edző).
   * MI VOLT A BAJ: a web eddig N darab `POST /events`-et küldött egy kliens-generált csoport-azonosítóval
   * (`EventCreateDialog.tsx` korábbi `createRecurringEvents`), részleges sikerrel — a szerver nem tudta
   * ellenőrizni sem a sorozatot, sem az ütközéseket egyben. Most minden-vagy-semmi: ütközésnél 400
   * `{ message, conflicts }`, és semmi nem jön létre. `dryRun: true` → előnézet (200), írás nélkül.
   * A `calendarService`-be került (nem az `eventService`-be), mert a dialógusok innen hívnak, és ez a
   * közös `api` példányon (401-kezelés, token-megújítás) megy.
   */
  async createSeries<D extends boolean>(
    request: CreateEventSeriesRequest & { dryRun: D }
  ): Promise<D extends true ? EventSeriesPreview : EventSeriesCreated> {
    const response = await api.post('/events/series', request);
    return response.data;
  },

  /** Meglévő sorozat bővítése (`templateEventId` = a sorozat egy aktív alkalma); válaszok mint a `createSeries`-nél. */
  async extendSeries<D extends boolean>(
    groupId: string,
    request: ExtendEventSeriesRequest & { dryRun: D }
  ): Promise<D extends true ? EventSeriesPreview : EventSeriesCreated> {
    const response = await api.post(`/events/series/${groupId}/extend`, request);
    return response.data;
  },

  /**
   * A sorozat törlése „innentől” (`fromDate` = `YYYY-MM-DD`, bezárólag). A kifizetett, a múltbeli, a rögzített
   * jelenlétű és a másik edzőhöz került alkalmak MEGMARADNAK (`kept`, `reasonCode`-dal).
   */
  async deleteSeries(groupId: string, fromDate: string): Promise<EventSeriesDeleteResult> {
    const response = await api.delete(`/events/series/${groupId}`, { params: { fromDate } });
    return response.data;
  },

  async registerForEvent(eventId: string, userRoleId: string): Promise<void> {
    await api.post(`/events/${eventId}/register`, { UserRoleId: userRoleId });
  },

  async unregisterFromEvent(eventId: string): Promise<void> {
    await api.delete(`/events/${eventId}/register`);
  },

  // Helyettesítés kérése magánórára (csak az eseményhez rendelt edző hívhatja).
  // Minden edző és admin értesítést kap; a backend zárja a jogosultságot.
  async requestSubstitution(eventId: string): Promise<void> {
    await api.post(`/events/${eventId}/request-substitution`);
  },

  async getEventParticipants(eventId: string): Promise<EventParticipant[]> {
    const response = await api.get(`/events/${eventId}/participants`);
    return response.data;
  },

  async addParticipantToEvent(eventId: string, userId: string, userRoleId: string): Promise<void> {
    await api.post(`/events/${eventId}/participants/add`, {
      userId,
      userRoleId
    });
  },

  async removeParticipantFromEvent(eventId: string, userId: string): Promise<void> {
    await api.delete(`/events/${eventId}/participants/${userId}`);
  }
};